import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { ApiService } from '../../core/api.service';
import { AuthService } from '../../core/auth.service';
import { I18nService } from '../../core/i18n.service';
import { TranslatePipe } from '../../core/translate.pipe';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  template: `
    <section class="page-header">
      <div>
        <h1>{{ 'profile.title' | t }}</h1>
        <p>{{ 'profile.description' | t }}</p>
      </div>
    </section>

    <div class="profile-grid">
      <section class="panel">
        <h2>{{ 'profile.details' | t }}</h2>
        <form class="record-form profile-form" [formGroup]="profileForm" (ngSubmit)="saveProfile()">
          <label>{{ 'login.username' | t }}<input [value]="auth.currentUser?.username || ''" readonly></label>
          <label>{{ 'users.role' | t }}<input [value]="(auth.currentUser?.role || '') | t" readonly></label>
          <label>{{ 'users.fullName' | t }}<input formControlName="full_name"></label>
          <label>{{ 'users.email' | t }}<input type="email" formControlName="email"></label>
          <div class="form-actions">
            <button type="submit" [disabled]="profileForm.invalid || savingProfile">{{ 'profile.save' | t }}</button>
          </div>
        </form>
        <p class="success" *ngIf="profileSuccess">{{ profileSuccess }}</p>
        <p class="error" *ngIf="profileError">{{ profileError }}</p>
      </section>

      <section class="panel">
        <h2>{{ 'profile.changePassword' | t }}</h2>
        <p class="muted">{{ 'profile.passwordHint' | t }}</p>
        <form class="record-form profile-form" [formGroup]="passwordForm" (ngSubmit)="changePassword()">
          <label>{{ 'profile.currentPassword' | t }}<input type="password" formControlName="current_password" autocomplete="current-password"></label>
          <label>{{ 'profile.newPassword' | t }}<input type="password" formControlName="new_password" autocomplete="new-password"></label>
          <label>{{ 'profile.confirmPassword' | t }}<input type="password" formControlName="confirm_password" autocomplete="new-password"></label>
          <div class="form-actions">
            <button type="submit" [disabled]="passwordForm.invalid || changingPassword">{{ 'profile.updatePassword' | t }}</button>
          </div>
        </form>
        <p class="error" *ngIf="passwordMismatch">{{ 'profile.passwordMismatch' | t }}</p>
        <p class="success" *ngIf="passwordSuccess">{{ passwordSuccess }}</p>
        <p class="error" *ngIf="passwordError">{{ passwordError }}</p>
      </section>
    </div>
  `
})
export class ProfileComponent {
  readonly auth = inject(AuthService);
  private readonly api = inject(ApiService);
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject(I18nService);

  savingProfile = false;
  changingPassword = false;
  profileSuccess = '';
  profileError = '';
  passwordSuccess = '';
  passwordError = '';

  profileForm = this.fb.nonNullable.group({
    full_name: [this.auth.currentUser?.full_name || '', Validators.required],
    email: [this.auth.currentUser?.email || '', Validators.email]
  });

  passwordForm = this.fb.nonNullable.group({
    current_password: ['', Validators.required],
    new_password: ['', [Validators.required, Validators.minLength(8)]],
    confirm_password: ['', [Validators.required, Validators.minLength(8)]]
  });

  get passwordMismatch(): boolean {
    const values = this.passwordForm.getRawValue();
    return !!values.confirm_password && values.new_password !== values.confirm_password;
  }

  saveProfile(): void {
    if (this.profileForm.invalid) return;
    this.savingProfile = true;
    this.profileSuccess = '';
    this.profileError = '';
    const value = this.profileForm.getRawValue();
    this.api.updateProfile({ full_name: value.full_name, email: value.email || null }).subscribe({
      next: (user) => {
        this.auth.updateCurrentUser(user);
        this.profileSuccess = this.i18n.t('profile.saved');
        this.savingProfile = false;
      },
      error: (err) => {
        this.profileError = err?.error?.detail || this.i18n.t('profile.saveFailed');
        this.savingProfile = false;
      }
    });
  }

  changePassword(): void {
    if (this.passwordForm.invalid || this.passwordMismatch) return;
    this.changingPassword = true;
    this.passwordSuccess = '';
    this.passwordError = '';
    const value = this.passwordForm.getRawValue();
    this.api.changePassword({
      current_password: value.current_password,
      new_password: value.new_password
    }).subscribe({
      next: () => {
        this.passwordForm.reset();
        this.passwordSuccess = this.i18n.t('profile.passwordChanged');
        this.changingPassword = false;
      },
      error: (err) => {
        this.passwordError = err?.error?.detail
          ? this.i18n.t(err.error.detail)
          : this.i18n.t('profile.passwordFailed');
        this.changingPassword = false;
      }
    });
  }
}
